/**
 * ActionRouter - Global delegated click router
 *
 * Eliminates per-module click listeners by routing any [data-action]
 * through a single capture-phase handler. Prevents propagation races
 * and duplicate bindings across re-renders.
 *
 * @typedef {Object} RouterContext
 * @property {Event} event
 * @property {HTMLElement} target
 * @property {Record<string,string>} [dataset]
 *
 * @typedef {(ctx: RouterContext) => any} ActionHandler
 *
 * @typedef {Object} ActionMeta
 * @property {boolean} [heavy]
 * @property {boolean} [destructive]
 * @property {boolean} [defer]
 * @property {number} [throttleMs]
 */

class ActionRouter {
  constructor(doc = document) {
    this.doc = doc;
    /** @type {Map<string, ActionHandler>} */
    this.handlers = new Map(); // action -> handler
    /** @type {Map<string, ActionMeta>} */
    this.meta = new Map(); // action -> meta
    this.namespaces = new Map(); // ns -> Set(action)
    this._busy = false;
    this._onClick = this._onClick.bind(this);
    this._installed = false;
    this._lastInvoke = new Map(); // action -> ts
    this._actionLog = [];
  }

  install() {
    if (this.disabled()) return;
    if (this._installed) return;
    this.doc.addEventListener('click', this._onClick, true); // capture
    this._installed = true;
  }

  uninstall() {
    if (!this._installed) return;
    this.doc.removeEventListener('click', this._onClick, true);
    this._installed = false;
  }

  /**
   * @param {string} namespace
   * @param {Record<string, ActionHandler|{handler:ActionHandler} & ActionMeta>} mapping
   */
  register(namespace, mapping) {
    const ns = namespace || 'global';
    const set = this.namespaces.get(ns) || new Set();
    const duplicates = [];
    Object.entries(mapping || {}).forEach(([action, value]) => {
      if (!action) return;
      let fn = null;
      let meta = {};
      if (typeof value === 'function') {
        fn = value;
      } else if (value && typeof value === 'object') {
        fn = value.handler || value.fn || null;
        meta = {
          heavy: !!value.heavy,
          destructive: !!value.destructive,
          defer: !!value.defer,
          throttleMs: typeof value.throttleMs === 'number' ? value.throttleMs : undefined
        };
      }
      if (typeof fn === 'function') {
        // Duplicate detection: keep first mapping by default
        if (this.handlers.has(action) && this.handlers.get(action) !== fn) {
          duplicates.push(action);
        } else {
          this.handlers.set(action, fn);
          if (meta && Object.keys(meta).length) this.meta.set(action, meta);
          set.add(action);
        }
      }
    });
    this.namespaces.set(ns, set);
    if (duplicates.length) {
      try {
        console.error('[ActionRouter] Duplicate action mapping detected:', { namespace: ns, actions: duplicates });
      } catch (_) {}
    }
    return () => this.unregister(ns);
  }

  unregister(namespace) {
    const ns = namespace || 'global';
    const set = this.namespaces.get(ns);
    if (!set) return;
    set.forEach(action => this.handlers.delete(action));
    this.namespaces.delete(ns);
  }

  /**
   * @param {string} action
   * @param {RouterContext} ctx
   */
  dispatch(action, ctx) {
    const fn = this.handlers.get(action);
    if (typeof fn === 'function') {
      const meta = this.meta.get(action) || {};
      // Throttle heavy/destructive actions to avoid double-fires
      const now = Date.now();
      const throttleMs = typeof meta.throttleMs === 'number' ? meta.throttleMs : (meta.heavy || meta.destructive ? 300 : 0);
      const last = this._lastInvoke.get(action) || 0;
      if (throttleMs && (now - last) < throttleMs) {
        this._log(action, ctx, 'throttled');
        return;
      }
      this._lastInvoke.set(action, now);

      const exec = () => {
        try {
          const result = fn(ctx);
          this._log(action, ctx, 'ok');
          return result;
        } catch (e) {
          this._log(action, ctx, 'error', e);
          console.error('ActionRouter handler error:', e);
        }
      };

      if (meta.defer) {
        setTimeout(exec, 0);
        return;
      }
      return exec();
    }
  }

  _onClick(e) {
    if (this.disabled()) return; // pass through
    const el = e.target?.closest?.('[data-action]');
    if (!el) return;
    const action = el.dataset.action;
    if (!action) return;
    // Only intercept if we actually have a handler
    const hasHandler = this.handlers.has(action);
    if (!hasHandler) return; // let it bubble normally

    // Optionally run in non-blocking mode by default
    const nonBlocking = this.nonBlocking();
    // Only intercept/stop propagation when a handler is registered
    if (!nonBlocking && hasHandler) {
      e.preventDefault();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      e.stopPropagation();
    }
    // Prevent cascaded handling within the same frame when we handle
    if (this._busy) return;
    this._busy = true;
    requestAnimationFrame(() => { this._busy = false; });
    this.dispatch(action, { event: e, target: el, dataset: { ...el.dataset } });
  }

  disabled() {
    // Hard-disable flag always respected
    if (typeof window !== 'undefined' && window.__DISABLE_ACTION_ROUTER__ === true) return true;
    // Dev-only local toggle; ignored in production to prevent broken PROD UIs
    try {
      const isDev = !!(import.meta && import.meta.env && import.meta.env.DEV);
      if (isDev && typeof localStorage !== 'undefined') {
        return localStorage.getItem('disableActionRouter') === 'true';
      }
    } catch (_) {}
    return false;
  }

  nonBlocking() {
    // Default is non-blocking to avoid swallowing clicks unless asked
    const flag = (typeof window !== 'undefined' && window.__ACTION_ROUTER_NON_BLOCKING__)
      || (typeof localStorage !== 'undefined' && localStorage.getItem('actionRouterNonBlocking'));
    // If flag explicitly set (string 'true' or boolean true), respect. Otherwise default true.
    if (flag === 'false' || flag === false) return false;
    return true;
  }

  _log(action, ctx, status = 'ok', error = null) {
    try {
      const rec = {
        time: new Date().toISOString(),
        action,
        status,
        ns: Array.from(this.namespaces.entries()).find(([ns, set]) => set.has(action))?.[0] || 'global',
        id: ctx?.target?.id || null,
        classes: ctx?.target?.className || null,
        error: error ? String(error?.message || error) : null
      };
      this._actionLog.push(rec);
      if (this._actionLog.length > 50) this._actionLog.shift();
      const shouldLog = (typeof window !== 'undefined' && (window.ACTION_LOG === true || window.__ACTION_LOG__ === true))
        || (typeof localStorage !== 'undefined' && localStorage.getItem('actionLog') === 'true');
      if (shouldLog) console.debug('➡️ ActionRouter:', rec);
      // Expose snapshot for diagnostics
      if (typeof window !== 'undefined') window.__ACTION_ROUTER_LAST__ = rec;

      // Lightweight analytics hook (opt-in via env + toggle)
      try {
        // Dynamic import to avoid hard dependency/cycles
        import('../services/AnalyticsService.js').then(mod => {
          const svc = mod?.default;
          if (svc && typeof svc.logAction === 'function') {
            svc.logAction(action, { ns: rec.ns, id: rec.id, classes: rec.classes, status: rec.status });
          }
        }).catch(() => {});
      } catch (_) {}
    } catch (_) {}
  }
}

// Singleton (opt‑in installation)
export const actionRouter = new ActionRouter();
try {
  const shouldInstall = (typeof window !== 'undefined' && window.__ENABLE_ACTION_ROUTER__ === true)
    || (typeof localStorage !== 'undefined' && localStorage.getItem('enableActionRouter') === 'true');
  if (shouldInstall) actionRouter.install();
} catch (_) {}

export default actionRouter;
